import React, { useMemo, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import {
  Alert,
  Box,
  Breadcrumbs,
  Button,
  Card,
  Chip,
  Link,
  Stack,
  Step,
  StepLabel,
  Stepper,
  Typography,
} from '@mui/material';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import ArrowForwardIcon from '@mui/icons-material/ArrowForward';
import VisibilityIcon from '@mui/icons-material/Visibility';
import SaveIcon from '@mui/icons-material/Save';
import PlayArrowIcon from '@mui/icons-material/PlayArrow';
import PublishIcon from '@mui/icons-material/Publish';
import useDocumentTitle from '../../../hooks/useDocumentTitle';
import { useGetConnectorHandler } from '../dataHandlers/getConnectorHandler';
import { useGetVersionHandler } from '../dataHandlers/getVersionHandler';
import {
  usePreviewVersionHandler,
  usePublishVersionHandler,
  useUpdateVersionHandler,
} from '../dataHandlers/mutateConnectorHandlers';
import { useConnectorJsonEditor } from '../hooks/useConnectorJsonEditor';
import SourcePayloadUploader from '../components/SourcePayloadUploader';
import RawConfigJsonEditor from '../components/RawConfigJsonEditor';
import MarminFieldReferencePanel from '../components/MarminFieldReferencePanel';
import MappingModeToggle from '../components/simpleMapper/MappingModeToggle';
import SimpleMappingBoard from '../components/simpleMapper/SimpleMappingBoard';
import { useFieldCatalogHandler } from '../dataHandlers/getSchemaHandler';
import PreviewPanel from '../components/PreviewPanel';
import { parseConnectorVersionParam } from '../utils/connectorRouteUtils';
import { mergeSftpPathPartyCatalog } from '../utils/fieldCatalogUtils';

const STEP_LABELS = ['Sample ERP', 'Mapping config', 'Preview', 'Publish'];
const STEP_COUNT = STEP_LABELS.length;

const VersionBuilderPage = () => {
  const { connectorKey, versionNumber } = useParams();
  const navigate = useNavigate();
  const decodedKey = decodeURIComponent(connectorKey);
  const versionNum = parseConnectorVersionParam(versionNumber);

  const { connector } = useGetConnectorHandler(decodedKey);
  const { version, isLoading, refetch } = useGetVersionHandler(decodedKey, versionNum);
  const { catalog: fieldCatalog } = useFieldCatalogHandler(connector?.document_type, version?.marmin_api_version);
  const simpleFieldCatalog = useMemo(
    () => mergeSftpPathPartyCatalog(fieldCatalog, connector?.document_type),
    [fieldCatalog, connector?.document_type],
  );

  useDocumentTitle(`v${versionNum} — ${connector?.name || decodedKey}`);

  const editor = useConnectorJsonEditor({ connector, version });
  const [activeStep, setActiveStep] = useState(0);
  const [previewResult, setPreviewResult] = useState(null);
  const [missingFields, setMissingFields] = useState([]);
  const [saveError, setSaveError] = useState('');

  const isEditable = version?.status === 'DRAFT' || version?.status === 'TESTING';

  const { mutateAsync: saveVersion, isPending: isSaving } = useUpdateVersionHandler();
  const { preview, isPreviewing, resetPreview } = usePreviewVersionHandler();
  const { mutateAsync: publishVersion, isPending: isPublishing } = usePublishVersionHandler({
    onSuccess: () => {
      refetch();
    },
  });

  const handleSave = async () => {
    setSaveError('');

    let rawConfig;
    try {
      rawConfig = editor.buildRawConfig();
    } catch (err) {
      setSaveError(err.message || 'Invalid mapping config JSON');

      throw err;
    }

    try {
      await saveVersion({
        connectorKey: decodedKey,
        versionNumber: versionNum,
        payload: { rawConfig },
      });
      refetch();
      setMissingFields([]);

      return rawConfig;
    } catch (error) {
      const missing = error?.response?.data?.missing;
      if (missing?.length) {
        setMissingFields(missing);
      }

      throw error;
    }
  };

  const handleRunPreview = async () => {
    if (!editor.samplePayload) {
      return;
    }

    try {
      await handleSave();
    } catch {
      return;
    }

    resetPreview();
    const result = await preview({
      connectorKey: decodedKey,
      versionNumber: versionNum,
      payload: {
        payload: editor.samplePayload,
        metadata: {
          documentType: connector.document_type,
          sourceSystem: connector.key,
        },
      },
    });
    setPreviewResult(result);
  };

  const handlePublish = async () => {
    try {
      await handleSave();
    } catch {
      return;
    }

    await publishVersion({ connectorKey: decodedKey, versionNumber: versionNum });
    navigate(`/connectors/${encodeURIComponent(decodedKey)}`);
  };

  const canProceedFromSample = Boolean(editor.samplePayload) && !editor.sampleParseError;
  const canProceedFromMapping = editor.canSave && isEditable;
  const canProceedFromPreview = previewResult !== null && !isPreviewing;

  const handleNext = async () => {
    if (activeStep === 1) {
      try {
        await handleSave();
      } catch {
        return;
      }
    }

    if (activeStep < STEP_COUNT - 1) {
      setActiveStep(activeStep + 1);
    }
  };

  const handlePrevious = () => {
    if (activeStep > 0) {
      setActiveStep(activeStep - 1);
    }
  };

  const pageScrollSx = {
    height: '86vh',
    overflowY: 'auto',
    overflowX: 'hidden',
    p: 1,
    pb: 4,
    color: '#fff',
    ml: { xs: 2, md: 6 },
    mr: { xs: 2, md: 6 },
  };

  if (isLoading || !version) {
    return (
      <Box sx={{ ...pageScrollSx, textAlign: 'center' }}>
        <Typography sx={{ pt: 4 }}>Loading version…</Typography>
      </Box>
    );
  }

  const detailPath = `/connectors/${encodeURIComponent(decodedKey)}/versions/${versionNum}`;
  const statusColor = version.status === 'ACTIVE' ? 'success' : version.status === 'DRAFT' ? 'default' : 'warning';

  const nextDisabled =
    (activeStep === 0 && !canProceedFromSample) ||
    (activeStep === 1 && !canProceedFromMapping) ||
    (activeStep === 2 && !canProceedFromPreview) ||
    activeStep >= STEP_COUNT - 1;

  return (
    <Box sx={pageScrollSx}>
      <Breadcrumbs sx={{ mb: 2 }}>
        <Link component="button" underline="hover" color="inherit" onClick={() => navigate('/connectors')}>
          Connectors
        </Link>
        <Link
          component="button"
          underline="hover"
          color="inherit"
          onClick={() => navigate(`/connectors/${encodeURIComponent(decodedKey)}`)}
        >
          {decodedKey}
        </Link>
        <Typography color="text.primary">v{versionNum} · builder</Typography>
      </Breadcrumbs>

      <Stack direction="row" spacing={1} sx={{ mb: 2 }} flexWrap="wrap" useFlexGap>
        <Button startIcon={<ArrowBackIcon />} onClick={() => navigate(`/connectors/${encodeURIComponent(decodedKey)}`)}>
          Back to versions
        </Button>
        <Button startIcon={<VisibilityIcon />} variant="outlined" onClick={() => navigate(detailPath)}>
          View field details
        </Button>
      </Stack>

      <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 1 }}>
        <Typography variant="h5" fontWeight={600}>
          Configure mapping — v{versionNum}
        </Typography>
        <Chip label={version.status} size="small" color={statusColor} />
      </Stack>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
        {connector?.document_type} · API {version.marmin_api_version}
      </Typography>

      {!isEditable && (
        <Alert severity="warning" sx={{ mb: 2 }}>
          Only DRAFT and TESTING versions can be edited or published.
        </Alert>
      )}

      <Stepper activeStep={activeStep} sx={{ mb: 3 }}>
        {STEP_LABELS.map((label) => (
          <Step key={label}>
            <StepLabel
              onClick={() => isEditable && setActiveStep(STEP_LABELS.indexOf(label))}
              sx={{ cursor: isEditable ? 'pointer' : 'default' }}
            >
              {label}
            </StepLabel>
          </Step>
        ))}
      </Stepper>

      {(missingFields.length > 0 || saveError) && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {saveError || `Required field mappings missing: ${missingFields.join(', ')}`}
        </Alert>
      )}

      <Card sx={{ p: 3, backgroundColor: 'background.paper', mb: 2 }}>
        {activeStep === 0 && (
          <SourcePayloadUploader
            inputMode={editor.sampleInputMode}
            onInputModeChange={editor.setSampleInputMode}
            value={editor.sampleJsonText}
            onChange={editor.setSampleJsonText}
            onApplyJson={editor.applySampleJson}
            onApplyXlsx={editor.applySampleXlsx}
            xlsxFileName={editor.xlsxFileName}
            parseError={editor.sampleParseError}
            samplePayload={editor.samplePayload}
            disabled={!isEditable}
          />
        )}

        {activeStep === 1 && (
          <>
            <Stack
              direction="row"
              justifyContent="space-between"
              alignItems="center"
              sx={{ mb: 2 }}
              flexWrap="wrap"
              useFlexGap
            >
              <Typography variant="h6">Mapping config</Typography>
              <MappingModeToggle value={editor.mappingMode} onChange={editor.setMappingMode} disabled={!isEditable} />
            </Stack>

            {editor.mappingModeError && (
              <Alert severity="error" sx={{ mb: 2 }}>
                {editor.mappingModeError}
              </Alert>
            )}

            <Alert severity="info" sx={{ mb: 2 }}>
              For SFTP XLSX uploads, map path party Profile ID or TIN from spreadsheet columns: Accounting supplier
              party (sale) or Accounting customer party (purchase). Publish validates these mappings.
            </Alert>

            {editor.mappingMode === 'simple' ? (
              <SimpleMappingBoard
                samplePayload={editor.samplePayload}
                fieldCatalog={simpleFieldCatalog}
                fields={editor.fieldsTree}
                onFieldsChange={editor.updateFieldsTree}
                disabled={!isEditable}
              />
            ) : (
              <>
                <RawConfigJsonEditor
                  value={editor.configJsonText}
                  onChange={editor.setConfigJsonText}
                  parseError={editor.configParseError}
                  onFormat={editor.formatConfigJson}
                  disabled={!isEditable}
                  hideTitle
                />
                <MarminFieldReferencePanel fieldCatalog={fieldCatalog} configJsonText={editor.configJsonText} />
              </>
            )}
          </>
        )}

        {activeStep === 2 && (
          <PreviewPanel
            result={previewResult}
            isLoading={isPreviewing}
            missingFields={missingFields}
            showTrace={false}
          />
        )}

        {activeStep === 3 && (
          <Box>
            <Typography variant="h6" sx={{ mb: 1 }}>
              Publish version
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
              Publishing makes this mapping active for production transforms. The current draft config and preview are
              saved before publish.
            </Typography>
            {previewResult && (
              <Alert severity={previewResult.success ? 'success' : 'warning'} sx={{ mb: 2 }}>
                Last preview: {previewResult.success ? 'succeeded' : 'completed with errors'} — review the Preview step
                if needed.
              </Alert>
            )}
            {!previewResult && (
              <Alert severity="info" sx={{ mb: 2 }}>
                Run preview on the previous step before publishing.
              </Alert>
            )}
          </Box>
        )}
      </Card>

      <Stack direction="row" justifyContent="space-between" alignItems="center" flexWrap="wrap" useFlexGap spacing={1}>
        <Button disabled={activeStep === 0} onClick={handlePrevious}>
          Previous
        </Button>

        <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
          {activeStep === 1 && isEditable && (
            <Button
              variant="outlined"
              startIcon={<SaveIcon />}
              disabled={!editor.canSave || isSaving}
              onClick={handleSave}
            >
              {isSaving ? 'Saving…' : 'Save draft'}
            </Button>
          )}

          {activeStep === 2 && isEditable && (
            <Button
              variant="outlined"
              startIcon={<PlayArrowIcon />}
              disabled={!editor.canPreview || isPreviewing}
              onClick={handleRunPreview}
            >
              {isPreviewing ? 'Running…' : 'Run preview'}
            </Button>
          )}

          {activeStep === 3 && isEditable && (
            <Button
              variant="contained"
              color="primary"
              startIcon={<PublishIcon />}
              disabled={isPublishing || !previewResult}
              onClick={handlePublish}
            >
              {isPublishing ? 'Publishing…' : 'Publish'}
            </Button>
          )}

          {activeStep < STEP_COUNT - 1 && (
            <Button
              variant="contained"
              endIcon={<ArrowForwardIcon />}
              disabled={nextDisabled || !isEditable}
              onClick={handleNext}
            >
              Next
            </Button>
          )}
        </Stack>
      </Stack>
    </Box>
  );
};

export default VersionBuilderPage;
